import React from 'react'
import { TwitterTweetEmbed } from 'react-twitter-embed'
import { Link } from '@ta-interaktiv/contentbricks'

export const TEXT_DE = {
  title: (
    <>
      Die dreistesten Versprechen der CS: «Verantwortung liegt in unserer DNA.»
    </>
  ),
  lead: (
    <>
      20 Jahre lang machte die Führung der Credit Suisse leere Versprechen. Hier
      zehn Beispiele - und was dann tatsächlich passierte.
    </>
  ),
  intro: (
    <>
      <p>
        «Die Wiederherstellung des Vertrauens in den Konzern ist die vielleicht
        wichtigste strategische Priorität.» sagte der Präsident der Credit
        Suisse. Aber er hiess nicht Axel Lehmann, nicht António Horta-Osório,
        nicht Urs Rohner und auch nicht Hans-Ulrich Doerig. Sondern Walter
        Kielholz. Und wir schreiben nicht das Jahr 2023, sondern das Jahr 2003.
      </p>
      <p>
        Der Niedergang der Credit Suisse hat eine lange Vorgeschichte. Immer
        wieder produzierte die Bank durch eigene Fehler riesige Skandale. Immer
        wieder erteilte die Finanzmarktaufsicht Finma scharfe Rügen. Und immer
        wieder verprachen die Manager, dass nun alles besser werde. Die
        blumigen, aber leeren Versprechen machte sie bis zum eigenen Untergang.
        Eine Chronologie:
      </p>
    </>
  ),
  updated: <>Aktualisiert am 21. März 2023</>,
  credits: ['Recherche und Text', 'Umsetzung', 'Mitarbeit'],
  promises: [
    {
      idx: 2000,
      year: '2000',
      promise: (
        <>
          <p>
            Anfang der 2000er-Jahre steht die Credit Suisse bereits
            international am Pranger. Sie muss zugeben, dass die nigerianische
            Diktatorenfamilie Abacha über 200 Millionen Franken bei der CS
            parkiert hat. Geld aus dem Volksvermögen, abgezweigt vom Despoten.
            Der Paradefall für schmutzige Diktatorengelder in der Schweiz
            scheint alle Klischees zu bestätigen.
          </p>

          <p className='indent'>
            «Da reise ich um die Welt und versichere, dass der Finanzplatz
            Schweiz sauber ist, und dann passiert so etwas», empört sich der
            damalige Finanzminister Kaspar Villiger. Die Finanzmarktaufsicht
            stellt in einer Untersuchung «teilweise gravierende Mängel» und
            «grobes individuelles Versagen beziehungsweise Fehlverhalten» fest.
          </p>

          <p className='indent'>
            Als Reaktion kommt 2001 das erste Versprechen.
          </p>
        </>
      )
    },
    {
      idx: 2001,
      imageId: 1,
      year: <>2001: «Korrekt, fair und ethisch»</>,
      name: <>Lukas Mühlemann</>,
      role: <>CS-Chef</>,
      promise: (
        <>
          <p>
            «Alle unsere Mitarbeiter erhalten einen Code of Conduct, und dort
            heisst es, dass man sich zuerst korrekt, fair und ethisch zu
            verhalten hat. (...) Das meinen wir todernst. Jeder weiss, dass er
            fliegt, wenn so etwas wie mit den Abacha-Geldern passiert.»
          </p>
        </>
      ),
      source: <>Magazin, 26. Januar 2001</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            Noch unter Mühlemann wird bekannt, dass 245 Millionen Franken aus
            der riesigen Korruptionsaffäre rund um den französischen Ölkonzern{' '}
            <b>Elf-Acquitaine</b> bei der Credit Suisse liegen. In den USA ist
            die Bank in die Bilanzmanipulationen beim Energieriesen <b>Enron</b>{' '}
            verwickelt. Und in Argentinien fliegt ihr die Beteiligung an einem
            dubiosen Finanzinstitut um die Ohren, was erneut die Schweizer
            Finanzmarktaufsicht auf den Plan ruft.
          </p>
        </>
      )
    },
    {
      idx: 2003,
      imageId: 2,
      year: <>2003: «Vertrauen ist wichtigste Priorität»</>,
      name: <>Walter Kielholz</>,
      role: <>CS-Präsident</>,
      promise: (
        <>
          <p>
            «Die Wiederherstellung des Vertrauens in den Konzern ist die
            vielleicht wichtigste strategische Priorität.» Vertrauenswürdig sei
            das Unternehmen dann, wenn «niemand die Wirksamkeit unseres
            Risikomanagements anzweifelt» und wenn die Aufsichtsbehörde «mit
            Respekt über uns spricht».
          </p>
        </>
      ),
      source: <>CS-Generalversammlung, 25. April 2003</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            2004 tauchen bei der CS schon wieder 60 Millionen Franken höchst
            fragwürdiger Herkunft auf – diesmal von der <b>japanischen Mafia</b>
            . Zur gleichen Zeit begeht die CS <b>Sanktionsbrüche</b> am
            Laufmeter. Die Bank wickelt über Schweizer Filialen bis 2006
            Zahlungen in Milliardenhöhe für Länder und Personen ab, die auf der
            Sanktionsliste der USA stehen. Besonders ins Gewicht fallen
            Zahlungen aus dem Iran.
          </p>

          <p className='indent'>
            Eine der Praktiken ist, dass CS-Mitarbeiter bei Transaktionen aus
            dem Iran den Namen der Auftraggeberbank im Swift-System einfach mit
            einer neutralen Bezeichnung ersetzen. Da steht dann etwa schlicht:
            «Order of a Customer». So verschleiert die CS die wahren
            Hintergründe der Transaktionen.
          </p>

          <p className='indent'>
            Später wird sich die CS mit den USA für diese Geschäfte auf{' '}
            <b>Strafzahlungen von 536 Millionen</b> Dollar einigen. «Das
            kriminelle Fehlverhalten der Credit Suisse in diesem Fall ist sowohl
            in seinem Ausmass als auch in seiner Komplexität schlichtweg
            verblüffend», sagt der US-Justizminister Eric Holder. Von der
            Finanzmarktaufsicht Finma gibt es eine scharfe Rüge.
          </p>
        </>
      )
    },
    {
      idx: 2007,
      imageId: 3,
      year: <>2007: «Höchste internationale Standards»</>,
      name: <>Brady Dougan, Walter Kielholz</>,
      role: <>CS-CEO, CS-Präsident</>,
      promise: (
        <>
          <p>
            «Wir sind bestrebt, konsequent die höchsten internationalen
            Standards punkto Ethik, Integrität und verantwortungsbewusstem
            Umgang mit der Gesellschaft und Umwelt zu erfüllen und deren
            Umsetzung durch umfassende interne Prüfungsprozesse
            sicherzustellen.»
          </p>
        </>
      ),
      source: <>Corporate Citizenship Report 2007</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            Mitglieder einer berüchtigten <b>bulgarischen Drogenbande</b>{' '}
            deponieren am CS-Hauptsitz von 2004 bis 2007 gleich kofferweise
            Bargeld. 54 Millionen Franken sind es insgesamt. Deswegen steht die
            Credit Suisse aktuell vor dem Bundesstrafgericht. Die
            Bundesanwaltschaft hat die Bank als Ganzes wegen Verdachts auf
            Geldwäscherei im grossen Stil angeklagt.
          </p>

          <p className='indent'>
            Die Ankläger werfen der CS ein <b>Versagen auf allen Ebenen</b> vor.
            Zu wenig Unterstützung für unerfahrene Kundenberater, schlechte
            IT-Systeme, mangelhafte Abklärung und Dokumentation von
            Risikotransaktionen und Kundenprofilen sowie die fehlende
            Gesamtsicht von Kundenaktivitäten sind nur einige der angeprangerten
            Mängel.
          </p>

          <p className='indent'>
            Anfang 2008 akzeptiert die CS ausserdem Millionen, die aus einem der
            prominentesten Wirtschaftsverbrechen in Russland stammen, dem
            sogenannten <b>Fall Magnitski</b>. Später folgen deswegen jahrelange
            Ermittlungen.
          </p>

          <p className='indent'>
            Parallel dazu betreibt die CS im grossen Stil das Geschäft mit{' '}
            <b>faulen US-Hypotheken</b>, die letztlich 2008 die Weltfinanzkrise
            auslösen. Auch hier bricht die CS die Regeln. Die Bank muss später
            den US-Behörden dafür total 5,28 Milliarden Dollar Busse und
            Entschädigung zahlen.
          </p>
        </>
      )
    },
    {
      idx: 2008,
      imageId: 2,
      year: <>2008: «Streng im Rahmen des Gesetzes»</>,
      name: <>Walter Kielholz</>,
      role: <>CS-Präsident</>,
      promise: (
        <>
          <p>
            «Wir haben das US-Crossborder-Geschäft streng im Rahmen der
            gesetzlichen Vorgaben betrieben. (...) Wir wissen von keiner
            Untersuchung der US-Steuerbehörden gegen uns.»
          </p>
        </>
      ),
      source: <>Finanz und Wirtschaft, 6. Dezember 2008</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            In Wahrheit hilft die Bank US-Bürgern bis 2009 beim{' '}
            <b>Hinterziehen von Steuern in grossem Stil</b>. Die CS muss später
            in den USA deswegen Strafen von 2,8 Milliarden Dollar bezahlen. Zum
            Vergleich: Die UBS, die damals ebenfalls bei Steuerdelikten half,
            musste «nur» 780 Millionen zahlen. In ihrem Schuldeingeständnis
            räumt die CS ein, jahrzehntelang illegal geschäftet und Tausenden
            US-Kunden «wissentlich und willentlich» beim Verstecken ihrer
            Vermögen vor dem Fiskus geholfen zu haben.
          </p>
          <p className='indent'>
            Die CS bricht nicht nur die US-Regeln. Die Finanzmarktaufsicht Finma
            rügt die Credit Suisse für ihr US-Geschäft «wegen schwerer
            Verletzung des Gewährs- und Organisationserfordernisses».
          </p>
          <p className='indent'>
            Und die Finma kümmert sich in diesen Jahren nicht nur wegen der
            unversteuerten US-Gelder um die CS. In einem anderen, bislang nicht
            öffentlich bekannten Verfahren kommt die Schweizer Aufsichtsbehörde
            zum Schluss, dass die CS von 2005 bis 2010 die{' '}
            <b>
              Sorgfaltspflichten in der Geldwäscherei-Abwehr wiederholt verletzt
            </b>{' '}
            hat. Konkret hat die Bank gemäss Finma-Untersuchung etwa die
            Herkunft des Vermögens von russischen Kunden nicht seriös abgeklärt.
          </p>
        </>
      )
    },
    {
      idx: 2011,
      imageId: 4,
      year: <>2011: «Erstklassige Compliance»</>,
      name: <>Walter Berchtold</>,
      role: <>Chef Private Banking</>,
      promise: (
        <>
          <p>
            «Es gibt Themen, wo erstklassige Compliance von höchster Bedeutung
            ist, beispielsweise bei der Geldwäscherei. Die strikte Einhaltung
            aller anwendbaren Gesetze ist für die Credit Suisse daher absolut
            grundlegend.»
          </p>
        </>
      ),
      source: <>Finanz und Wirtschaft, 28. Mai 2011</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            In Hongkong vergibt die Bank Jobs an Familienmitglieder und Freunde
            von hochrangigen chinesischen Beamten, um lukrative
            Investmentbanking-Aufträge zu sichern. Wegen{' '}
            <b>Nepotismus und Korruption</b> zahlt die CS später dafür 77
            Millionen Dollar an US-Behörden. Eine US-Filiale der CS verstösst
            zudem gegen Anti-Geldwäscherei-Vorschriften und wird später deswegen
            mit 16,5 Millionen Dollar gebüsst.{' '}
          </p>
          <p className='indent'>
            Auch in der Schweiz braut sich neues Ungemach zusammen, diesmal in
            der Genfer Filiale. Dort{' '}
            <b>
              zweigt ein Starbanker der CS gemäss einer Klage über 120 Millionen
              von einem Grosskunden der Bank ab
            </b>
            . Später wird er zu fünf Jahren Haft verurteilt und begeht Suizid.
            Die Finma stellt auch in diesem Fall eklatante Mängel im
            Kontrollsystem und Risikomanagement der CS fest. Obwohl der
            Starbanker jahrelang Vorschriften verletzte, disziplinierte ihn die
            CS nicht. Im Gegenteil: Die Bank belohnte ihn mit Millionenboni.
          </p>
        </>
      )
    },
    {
      idx: 2013,
      imageId: 'logo',
      year: <>2013: «Einsatz für Benachteiligte»</>,
      name: <>Credit Suisse</>,
      promise: (
        <>
          <p>
            «Wir setzen uns für die Verbesserung der Lebensumstände
            benachteiligter Menschen ein.»
          </p>
        </>
      ),
      source: <>Bericht Unternehmerische Verantwortung 2013</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            Die CS ist entscheidend daran beteiligt,{' '}
            <b>ein ganzes afrikanisches Land in den Ruin zu treiben</b>:
            Moçambique. Über ihre Filiale in London gewährt die CS 2013 zwei
            moçambiquanischen Staatsfirmen einen Milliardenkredit, der dann zu
            grossen Teilen an korrupte Beamte fliesst und das Land in
            Südostafrika zahlungsunfähig macht – mit drastischen Folgen für die
            bitterarme Bevölkerung.
          </p>
          <p className='indent'>
            Später muss die CS deswegen in den USA erneut Geldstrafen von rund
            einer halben Milliarde Dollar zahlen. Die Finma kommt in ihrer
            eigenen Untersuchung zum Schluss, dass das Risikomanagement der CS «
            <b>schwerwiegende Mängel</b> aufwies» und die Bank überdies auch
            schwer gegen die Pflicht verstiess, einen Verdacht auf Geldwäscherei
            zu melden.
          </p>
          <p className='indent'>
            Doch das ist nicht alles. Als die Strafverfolger der Schweiz und der
            USA 2015 korrupte Fussballfunktionäre ins Visier nehmen, tauchen
            abermals Credit-Suisse-Konten auf, über die Millionen gewaschen
            wurden.{' '}
            <b>
              Südamerika wird zum Brennpunkt. Die Finma startet nun gleich drei
              Untersuchungen
            </b>
            : eine wegen der Fifa-Gelder, eine wegen Geldern aus dem
            Milliarden-Korruptionsfall rund um den brasilianischen Ölkonzern
            Petrobras und eine wegen korrupter Gelder aus dem krisengeplagten
            Venezuela.{' '}
          </p>
          <p className='indent'>
            In allen drei Fällen kommt die Finma später zum Schluss, dass die CS
            «über Jahre hinweg und wiederholt» gegen elementare
            «aufsichtsrechtliche Pflichten zur Bekämpfung der Geldwäscherei»
            verstossen hat. Dies im Zeitraum bis Ende 2016, also mehr als 15
            Jahre nach dem Skandal um die Abacha-Gelder.
          </p>
          <p className='indent'>
            Im O-Ton der Finma: «Die festgestellten Verfehlungen sind keine
            Einzelfälle und beziehen sich auch nicht auf isolierte
            Organisationseinheiten oder (länderspezifische) Kundensegmente der
            CS. Sämtliche Prüfberichte — Fifa, PDVSA, Petrobras — kommen
            übergreifend zu einem ähnlichen Ergebnis und zeigen dasselbe Muster
            hinsichtlich der Verletzung der Geldwäscherei-Sorgfaltspflichten
            auf.{' '}
            <b>
              Die Nichteinhaltung der Sorgfaltspflichten innerhalb der Bank ist
              demnach ein gesamtheitliches Problem
            </b>
            , welches das gesamte Geldwäscherei-Dispositiv der Bank massgeblich
            betrifft.»
          </p>
        </>
      )
    },
    {
      idx: 2016,
      imageId: 'logo',
      year: <>2016: «Verantwortung in unserer DNA»</>,
      name: <>Credit Suisse</>,
      promise: (
        <>
          <p>
            «Gesellschaftliche und unternehmerische Verantwortung liegt in
            unserer DNA.»{' '}
          </p>
        </>
      ),
      source: <>Zeitungsinserat, 21. November 2016</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            Unter dem neuen CEO Tidjane Thiam beginnt die CS damit,{' '}
            <b>Mitarbeiter von Privatdetektiven überwachen zu lassen</b>. Sieben
            Personen nimmt die Bank zwischen 2016 und 2019 ins Visier, darunter
            auch Externe. Dem Chef der Vermögensverwaltung fahren die Detektive
            mit dem Auto hinterher und fotografieren auch dessen Frau. Ins
            Visier gerät sogar der Ex-Mann von Thiams Freundin, der nicht bei
            der Bank arbeitet.
          </p>
          <p className='indent'>
            Wieder schreitet die Finma ein, wieder stellt sie «erhebliche
            Mängel» fest, diesmal in der Corporate Governance der CS. In den
            meisten Fällen sei formlos und ohne nachvollziehbare Begründung über
            die Observationen entschieden worden, obwohl diese grosse
            Reputationsrisiken mit sich brachten. Deren Hintergründe habe das
            Management verschleiert, einmal sogar, indem eine Rechnung
            nachträglich abgeändert wurde. Das Ganze zeuge von einer{' '}
            <b>
              «unangemessenen Unternehmenskultur bei Teilen der damaligen
              operativen Führung der Credit Suisse»
            </b>
            , so die Finma.
          </p>
          <p className='indent'>
            Im <b>Vatikan</b> entwickelt sich derweil ein mutmasslicher
            Millionenbetrug mit Spendengeldern. Grosse Summen landen auf Konten
            der CS in der Schweiz. Und in <b>London, Paris und Amsterdam</b>{' '}
            durchsuchen Behörden in einer gross angelegten Aktion CS-Filialen
            wegen Verdachts auf Steuerbetrug und Geldwäscherei. Zeitungen
            berichten von rund 55’000 verdächtigten Konten.
          </p>
          <p className='indent'>
            Im Dezember 2020 erhebt die Bundesanwaltschaft Anklage im Fall der
            bulgarischen Drogenbande, deren Mitglieder bis 2007 mit Geldkoffern
            an den Paradeplatz kamen. Fast zeitgleich teilt die US-Notenbank
            Federal Reserve mit, dass sie bei der{' '}
            <b>Geldwäscherei-Abwehr der CS in den USA Mängel</b> festgestellt
            habe, und gibt der Bank 90 Tage Zeit, diese zu beheben.
          </p>
        </>
      )
    },
    {
      idx: 2020,
      imageId: 'logo',
      year: <>2020: «Einhaltung aller Vorgaben»</>,
      name: <>Credit Suisse</>,
      promise: (
        <>
          <p>
            «Das bankinterne Abwehrdispositiv zur Geldwäschereibekämpfung wurde
            in den vergangenen zwölf und mehr Jahren, und insbesondere in
            jüngerer Zeit (...) massiv ausgebaut und verstärkt. Regelkonformes
            Wachstum unter Einhaltung aller rechtlichen und regulatorischen
            Vorgaben hat für die Credit Suisse höchste Priorität.»{' '}
          </p>
        </>
      ),
      source: <>Medienmitteilung, 17. Dezember 2020</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            Nun wird es noch einmal richtig teuer. Trotz eindeutigen
            Alarmzeichen verspekuliert sich die Credit Suisse in
            Hochrisiko-Geschäften, von denen andere Grossbanken die Finger
            lassen. In den Skandalen um die <b>Greensill-Fonds</b> und den
            Vermögensverwalter <b>Archegos</b> muss die CS schliesslich
            Milliarden abschreiben. Allein bei Archegos beträgt der{' '}
            <b>Verlust über fünf Milliarden</b>.
          </p>
          <p className='indent'>
            Im Februar 2023 schliesst die Finma ihre Greensill-Untersuchung ab.
            Sie konstatiert - einmal mehr -, dass die Credit Suisse «mit Blick
            auf das Risikomanagement und eine angemessene Betriebsorganisation{' '}
            <b>
              in schwerer Weise gegen die aufsichtsrechtlichen Pflichten
              verstossen
            </b>
            » hat. Die Grossbank habe es jahrelang versäumt, «Risiken angemessen
            zu erfassen, zu begrenzen und zu überwachen».
          </p>
          <p className='indent'>
            Nach all diesen Skandalen ist es für den langjährigen CS-Präsidenten
            Urs Rohner Zeit, zu gehen. Es folgt ein kurzes Intermezzo mit
            António Horta-Osório, der gleich weiter macht mit den Versprechen.
          </p>
        </>
      )
    },
    {
      idx: 2021,
      imageId: 5,
      year: <>2021: «Im Herzen ein Risikomanager»</>,
      name: <>António Horta-Osório</>,
      role: <>CS-Präsident</>,
      promise: (
        <>
          <p>
            «Ich habe darauf insistiert, dass das Risikomanagement höchste
            Priorität bekommt. Jeder Banker muss im Herzen ein Risikomanager
            sein. Zudem müssen wir eine Kultur der persönlichen Verantwortung
            entwickeln.»
          </p>
        </>
      ),
      source: <>NZZ, 1. Juli 2021</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            Ende 2021 verstösst Horta-Osório mehrfach gegen die
            Corona-Quarantäne-Regeln. Er tritt schon im Januar 2022 wieder
            zurück.
          </p>

          <p className='indent'>
            Einen Monat später veröffentlichen Journalisten das{' '}
            <b>Datenleck «Suisse Secrets»</b>. Es zeigt, dass die CS bis
            mindestens Mitte des letzten Jahrzehnts Konten für{' '}
            <b>zahlreiche Autokraten und Schwerverbrecher</b> führte, darunter
            gar Foltergeneräle.
          </p>

          <p className='indent'>
            Auf Horta-Osório folgt Axel Lehmann. Er sagt im Oktober 2022:
          </p>
        </>
      )
    },
    {
      idx: 2022,
      imageId: 6,
      year: <>2022: «Die Aktionäre werden profitieren»</>,
      name: <>Axel Lehmann</>,
      role: <>CS-Präsident</>,
      promise: (
        <>
          {' '}
          <p>
            «Was wir heute machen, ist nicht einfach nochmals etwas
            reorganisieren, sondern wir stellen wirklich fundamental die Weichen
            für die Zukunft, so dass wir eine viel bessere Balance haben. (…)
            Ich glaube, die Aktionärinnen und Aktionäre werden profitieren, weil
            wir eine klare Strategie haben, ein weniger risikoreiches Geschäft
            machen und uns für eine langfristig erfolgreiche Zukunft
            positionieren.»
          </p>
        </>
      ),
      source: <>Interview SRF, 27. Oktober 2022</>,
      subheader: <>Das geschah:</>,
      reality: (
        <>
          <p>
            Zwanzig Jahre nachdem der damalige CS-Präsident Walter Kielholz die
            Wiederherstellung des Vertrauens als oberste Priorität ausgerufen
            hat, ist das Vertrauen in die Bank vollends zerstört. Für 2022 muss
            die CS einen <b>Verlust von über sieben Milliarden Franken</b>{' '}
            bekannt geben. Grossaktionäre springen ab. Die US-Börsenaufsicht
            verhindert die Publikation des Jahresberichts wegen offenen Fragen -
            ein äusserst ungewöhnlicher Vorgang. Am Schluss ziehen die Kundinnen
            und Kunden sage und schreibe 10 Milliarden Franken Vermögen von der
            CS ab - pro Tag.
          </p>
          <p className='indent'>
            Der Bundesrat muss tun, was er nach der UBS-Rettung 2008 nie mehr
            tun wollte: eine systemrelevante Bank retten - und dabei Milliarden
            an Volksvermögen aufs Spiel setzen. Eiligst vermittelt die
            Landesregierung einen Deal, in dem <b>die UBS die CS übernimmt</b>{' '}
            und die Risiken dabei grösstenteils auf den Staat abwälzen kann.
            Aktionäre und Inhaberinnen von Anleihen verlieren Milliarden,
            Tausende Arbeitsplätze stehen auf dem Spiel.
          </p>
          <p className='indent'>
            Die CS teilt derweil ihren Kundinnen und Stakeholdern mit, dass sie
            mit der UBS einen «Fusionsvertrag» abgeschlossen habe. Und -
            natürlich - macht sie wieder ein Versprechen: Der Zusammenschluss
            sei «im besten Interesse der Kundinnen und Kunden, der Anlegerinnen
            und Anlegern sowie weiterer Anspruchsgruppen» und «zur
            Wiederherstellung des Vertrauens und der Stabilität der
            Finanzmärkte».
          </p>

          {/* <TwitterTweetEmbed tweetId='1485275228351700994' /> */}
        </>
      )
    }
  ]
}
